import type { ConversationEvent, ConversationEventType } from '@callhub/shared';
import type { VoiceCallStatus } from '@/lib/twilio-voice';

const DELTA_EVENT_TYPES: ConversationEventType[] = [
  'voice_transcript',
  'call_started',
  'call_ended',
];
const POST_CALL_DELTA_EVENT_TYPES: ConversationEventType[] = [
  ...DELTA_EVENT_TYPES,
  'summary',
  'highlight',
  'action_item',
];
const DELTA_FETCH_LIMIT = 120;
const FULL_SYNC_POLL_INTERVAL = 20;
const MAX_MERGED_EVENTS = 400;

export type DialerEventFetchPlan =
  | { mode: 'full' }
  | {
      mode: 'delta';
      after: string;
      types: ConversationEventType[];
      limit: number;
    };

function isRealtimeVoiceStatus(status: VoiceCallStatus, hasActiveCall: boolean): boolean {
  if (status === 'calling' || status === 'ringing') return true;
  return status === 'in-progress' && hasActiveCall;
}

export function resolveDialerEventFetchPlan(input: {
  callStatus: VoiceCallStatus;
  hasActiveCall: boolean;
  latestKnownEventAt: string | null;
  pollsSinceFullSync: number;
  recentRealtimeTail: boolean;
}): DialerEventFetchPlan {
  const realtimeNow = isRealtimeVoiceStatus(input.callStatus, input.hasActiveCall);
  const shouldUseDelta = realtimeNow || input.recentRealtimeTail;
  if (!shouldUseDelta) {
    return { mode: 'full' };
  }

  if (!input.latestKnownEventAt) {
    return { mode: 'full' };
  }

  const latestKnownMs = Date.parse(input.latestKnownEventAt);
  if (Number.isNaN(latestKnownMs)) {
    return { mode: 'full' };
  }

  if (input.pollsSinceFullSync >= FULL_SYNC_POLL_INTERVAL) {
    return { mode: 'full' };
  }

  return {
    mode: 'delta',
    after: new Date(latestKnownMs).toISOString(),
    types: realtimeNow ? DELTA_EVENT_TYPES : POST_CALL_DELTA_EVENT_TYPES,
    limit: DELTA_FETCH_LIMIT,
  };
}

export function mergeConversationEventTimeline(
  existing: ConversationEvent[],
  incoming: ConversationEvent[],
  maxEvents: number = MAX_MERGED_EVENTS,
): ConversationEvent[] {
  if (incoming.length === 0) return existing;

  const byId = new Map<string, ConversationEvent>();
  for (const event of existing) {
    byId.set(event.id, event);
  }
  for (const event of incoming) {
    byId.set(event.id, event);
  }

  const sorted = [...byId.values()].sort((left, right) => {
    const leftMs = Date.parse(left.createdAt);
    const rightMs = Date.parse(right.createdAt);
    const safeLeft = Number.isNaN(leftMs) ? 0 : leftMs;
    const safeRight = Number.isNaN(rightMs) ? 0 : rightMs;
    return safeRight - safeLeft;
  });

  if (!Number.isFinite(maxEvents) || maxEvents < 1) {
    return sorted;
  }

  return sorted.slice(0, Math.floor(maxEvents));
}
